import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Radio, X, AlertTriangle } from "lucide-react";

const HiveContext = createContext(null);
export const useHive = () => useContext(HiveContext);

const POLL_MS = 45000;

function summarize(e) {
  const p = e.payload || {};
  if (Array.isArray(p.players) && p.players.length) return p.players.map((x) => x.name || x).filter(Boolean).join(", ");
  if (p.kind) return String(p.kind).replace(/_/g, " ");
  if (p.note) return String(p.note);
  if (p.scan) return `${p.league || p.scan} ${p.season || ""}`.trim();
  return e.type;
}

const eid = (e) => e.id || `${e.type}:${e.ts || e.created_at || e.time || ""}:${JSON.stringify(e.payload || {}).slice(0, 60)}`;

function HiveBanner({ event, onClose }) {
  const from = event.ship_from || event.payload?.ship || "fleet";
  return (
    <div className="fixed inset-x-0 top-0 z-[9999] flex justify-center px-3 pt-3" data-testid="hive-critical-banner">
      <div className="flex w-full max-w-3xl items-center gap-3 rounded-xl border border-red-500/40 bg-[#160606]/95 px-4 py-3 shadow-[0_0_30px_rgba(255,59,48,0.35)] backdrop-blur-md">
        <span className="relative flex h-2.5 w-2.5 shrink-0">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-70" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
        </span>
        <AlertTriangle size={16} className="shrink-0 text-red-400" />
        <div className="min-w-0 flex-1">
          <p className="truncate font-tac text-[10px] tracking-[0.25em] text-red-300">AVA HIVE · {String(event.type).toUpperCase()} · {from}</p>
          <p className="truncate text-sm text-white/90">{summarize(event)}</p>
        </div>
        <button onClick={onClose} className="shrink-0 rounded-lg p-1 text-white/50 hover:bg-white/10 hover:text-white" data-testid="hive-banner-close">
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

export function HiveProvider({ children }) {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [critical, setCritical] = useState(null);
  const [linked, setLinked] = useState(false);
  const seen = useRef(new Set());
  const timer = useRef(null);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/hive/inbox");
      if (!data.error) setLinked(true);
      const evs = data.events || [];
      const fresh = [];
      for (const e of evs) {
        const id = eid(e);
        if (seen.current.has(id)) continue;
        seen.current.add(id);
        fresh.push({ ...e, _id: id });
      }
      if (fresh.length) {
        setEvents((prev) => [...fresh, ...prev].slice(0, 40));
        // Inbound fleet threat/alert notifications are silenced by user preference —
        // events are still collected (for a future feed page) but no toast/banner is surfaced.
      }
    } catch (_) {
      setLinked(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    poll();
    timer.current = setInterval(poll, POLL_MS);
    return () => clearInterval(timer.current);
  }, [user, poll]);

  return (
    <HiveContext.Provider value={{ events, critical, linked, dismiss: () => setCritical(null) }}>
      {children}
      {critical && <HiveBanner event={critical} onClose={() => setCritical(null)} />}
    </HiveContext.Provider>
  );
}
